import { z } from 'zod';

/**
 * Wave 24 — NFe XML ingestion.
 *
 * A supplier sends the crew a Brazilian Nota Fiscal Eletrônica (NFe)
 * XML. Instead of re-typing the fields, the crew uploads the XML on
 * the PO detail page and we transcribe the government-validated
 * numbers into a `payments` row automatically.
 *
 * The **chave de acesso** is a 44-digit identifier encoded by SEFAZ:
 * UF(2) + AAMM(4) + CNPJ(14) + mod(2) + série(3) + nNF(9) + tpEmis(1)
 * + cNF(8) + cDV(1). It's globally unique by construction, which makes
 * it the natural dedup key for an ingest operation.
 *
 * We keep the parsed metadata close to what a human actually needs to
 * see on screen (CNPJ, razão social, nº/série, data, total) and a
 * verbatim `chaveAcesso` to drive the dedup path.
 */

/** 44-digit-only chave de acesso. We strip non-digits before validating. */
export const nfeChaveAcessoSchema = z
  .string()
  .regex(/^\d{44}$/u, 'chaveAcesso deve conter exatamente 44 dígitos');

export const nfeMetadataSchema = z.object({
  chaveAcesso: nfeChaveAcessoSchema,
  cnpjEmitente: z
    .string()
    .regex(/^\d{14}$/u, 'CNPJ emitente deve conter exatamente 14 dígitos'),
  razaoSocialEmitente: z.string().min(1).max(255),
  nomeFantasiaEmitente: z.string().max(255).nullable(),
  numeroNF: z.string().min(1).max(20),
  serie: z.string().max(10),
  /** ISO UTC. dhEmi (datetime) is preferred; dEmi (date-only) is padded to T00:00:00Z. */
  emittedAt: z.string(),
  /** Total in cents (vNF parsed as decimal reais × 100, rounded). */
  valorTotalCents: z.number().int().min(0),
  /** nProt from `<infProt>`, when present (authorized NFe only). */
  numeroProtocolo: z.string().max(40).nullable(),
});
export type NfeMetadata = z.infer<typeof nfeMetadataSchema>;

/** Dry-run parse result. */
export const nfeParseResultSchema = z.object({
  metadata: nfeMetadataSchema,
});
export type NfeParseResult = z.infer<typeof nfeParseResultSchema>;

/**
 * Ingest body — the raw XML as a string. We don't accept multipart here
 * because the XML is small (< 20 KB typically) and a plain text/xml
 * body is simpler to reason about and easier to unit-test.
 */
export const nfeIngestRequestSchema = z.object({
  xml: z.string().min(1).max(200_000),
  /**
   * Optional payment-method hint. When absent, we default to 'transfer'
   * because the real settlement method is chosen later when the crew
   * actually pays (PIX/boleto/transfer). A NFe arriving ≠ money moving.
   */
  method: z
    .enum(['pix', 'boleto', 'transfer', 'cash', 'other'])
    .optional(),
});
export type NfeIngestRequest = z.infer<typeof nfeIngestRequestSchema>;

/** Ingest response: the created payment id + the parsed metadata. */
export const nfeIngestResultSchema = z.object({
  paymentId: z.string(),
  metadata: nfeMetadataSchema,
  /**
   * True when the emitente CNPJ in the XML matches the PO's supplier
   * CNPJ. False is not a hard error — crews sometimes invoice via a
   * related CNPJ within the same group — but the UI should surface a
   * "check this" badge so the PM can eyeball it before settlement.
   */
  supplierCnpjMatches: z.boolean(),
});
export type NfeIngestResult = z.infer<typeof nfeIngestResultSchema>;
